import React, { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom';
import { QRCodeSVG } from "qrcode.react";
import logomainsvg from "@/assets/logo-main.svg";

const { VITE_ENV } = import.meta.env;

const CheckIn = () => {
    const [searchParams] = useSearchParams();
    const token = (searchParams.get("t") || "").trim();
    const [deviceId, setDeviceId] = useState<string>("");

    // Generate device fingerprint based on stable device characteristics
    useEffect(() => {
        const generateDeviceFingerprint = () => {
            // Check if device fingerprint already exists in localStorage
            let storedDeviceId = localStorage.getItem('device_fingerprint_id');
            
            if (!storedDeviceId) {
                // Collect stable device characteristics for fingerprinting
                const fingerprint = {
                    // Browser and system info (stable)
                    userAgent: navigator.userAgent,
                    language: navigator.language,
                    
                    // Hardware characteristics (very stable)
                    screenResolution: `${screen.width}x${screen.height}`,
                    screenColorDepth: screen.colorDepth,
                    screenPixelDepth: screen.pixelDepth,
                    availableScreenSize: `${screen.availWidth}x${screen.availHeight}`,
                    
                    // System capabilities (stable)
                    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                    touchSupport: 'ontouchstart' in window,
                    maxTouchPoints: navigator.maxTouchPoints || 0,
                    cookieEnabled: navigator.cookieEnabled,
                    
                    // Hardware specs (stable)
                    hardwareConcurrency: navigator.hardwareConcurrency || 'unknown',
                    
                    // Canvas fingerprinting (very stable and unique)
                    canvasFingerprint: (() => {
                        try {
                            const canvas = document.createElement('canvas');
                            const ctx = canvas.getContext('2d');
                            if (!ctx) return 'unsupported';
                            
                            // Draw text with specific font and style
                            ctx.textBaseline = 'top';
                            ctx.font = '14px Arial';
                            ctx.fillText('Device fingerprinting test 0123456789', 2, 2);
                            
                            // Get canvas data URL
                            return canvas.toDataURL();
                        } catch {
                            return 'error';
                        }
                    })(),
                    
                    // WebGL fingerprinting (hardware-specific)
                    webGLVendor: (() => {
                        try {
                            const canvas = document.createElement('canvas');
                            const gl = canvas.getContext('webgl') as WebGLRenderingContext | null;
                            if (gl) {
                                const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
                                return debugInfo ? gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) : 'unknown';
                            }
                            return 'unsupported';
                        } catch {
                            return 'error';
                        }
                    })(),
                    
                    webGLRenderer: (() => {
                        try {
                            const canvas = document.createElement('canvas');
                            const gl = canvas.getContext('webgl') as WebGLRenderingContext | null;
                            if (gl) {
                                const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
                                return debugInfo ? gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) : 'unknown';
                            }
                            return 'unsupported';
                        } catch {
                            return 'error';
                        }
                    })(),
                    
                    // Audio fingerprinting (very stable)
                    audioFingerprint: (() => {
                        try {
                            const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
                            const oscillator = audioContext.createOscillator();
                            const analyser = audioContext.createAnalyser();
                            const gainNode = audioContext.createGain();
                            const scriptProcessor = audioContext.createScriptProcessor(4096, 1, 1);
                            
                            gainNode.gain.value = 0; // Silent
                            oscillator.type = 'triangle';
                            
                            oscillator.connect(analyser);
                            analyser.connect(scriptProcessor);
                            scriptProcessor.connect(gainNode);
                            gainNode.connect(audioContext.destination);
                            
                            oscillator.start(0);
                            
                            const audioData = new Float32Array(analyser.frequencyBinCount);
                            analyser.getFloatFrequencyData(audioData);
                            
                            oscillator.stop();
                            audioContext.close();
                            
                            // Create hash from audio data
                            return Array.from(audioData.slice(0, 100)).join(',');
                        } catch {
                            return 'error';
                        }
                    })(),
                    
                    // Font fingerprinting (stable)
                    fontFingerprint: (() => {
                        const fonts = [
                            'Arial', 'Verdana', 'Times New Roman', 'Courier New', 
                            'Georgia', 'Palatino', 'Garamond', 'Bookman', 
                            'Comic Sans MS', 'Trebuchet MS', 'Arial Black', 'Impact'
                        ];
                        
                        const canvas = document.createElement('canvas');
                        const ctx = canvas.getContext('2d');
                        if (!ctx) return 'unsupported';
                        
                        const baseFont = 'monospace';
                        ctx.font = `12px ${baseFont}`;
                        const baseWidth = ctx.measureText('abcdefghijklmnopqrstuvwxyz0123456789').width;
                        
                        const fontWidths = fonts.map(font => {
                            ctx.font = `12px ${font}`;
                            const width = ctx.measureText('abcdefghijklmnopqrstuvwxyz0123456789').width;
                            return `${font}:${width}`;
                        });
                        
                        return fontWidths.join('|');
                    })()
                };
                
                // Create a proper hash from the fingerprint
                const fingerprintString = JSON.stringify(fingerprint);
                
                // Simple but effective hash function
                let hash = 0;
                for (let i = 0; i < fingerprintString.length; i++) {
                    const char = fingerprintString.charCodeAt(i);
                    hash = ((hash << 5) - hash) + char;
                    hash = hash & hash; // Convert to 32-bit integer
                }
                
                // Convert to hex and take first 16 characters
                storedDeviceId = Math.abs(hash).toString(16).padStart(8, '0') + 
                                Math.abs(hash * 31).toString(16).padStart(8, '0');
                
                // Store it for future use
                localStorage.setItem('device_fingerprint_id', storedDeviceId);
                
                console.log('Generated device fingerprint:', {
                    fingerprintData: fingerprint,
                    deviceId: storedDeviceId
                });
            }
            
            return storedDeviceId;
        };

        setDeviceId(generateDeviceFingerprint());
    }, []);

    if (!token) {
        return (
            <div className="flex flex-col items-center justify-center h-screen">
                <h2 className="text-lg font-bold mb-2">No token provided</h2>
                <p className="text-gray-600">Provide a valid token in the URL as ?t=...</p>
            </div>
        )
    }
    
    console.log(`${token},${deviceId}`)

    return (
        <div className="flex flex-col items-center justify-center h-screen">
            <h2 className="text-lg font-bold mb-4">Scan the QR Code</h2>
            <QRCodeSVG
                value={`${token},${deviceId}`}
                size={256}

            />
            <p className="mt-4 text-center text-gray-600">
                This QR encodes your facility access.
            </p>
            {VITE_ENV == "development" && deviceId && (
                <p className="mt-2 text-xs text-gray-400 text-center">
                    Device Fingerprint: {deviceId}
                </p>
            )}
        </div>
    )
}

export default CheckIn